import type { ApolloClient, DocumentNode } from '@apollo/client';
import {
  AddEntryDocument,
  type AddEntryInput,
  type AddEntryMutation,
  AddZoneDocument,
  type AddZoneInput,
  type AddZoneMutation,
  AssignReaderDocument,
  type AssignReaderInput as AssignReaderCommandInput,
  type AssignReaderMutation,
  CreateSiteLayoutDraftDocument,
  type CreateSiteLayoutDraftInput,
  type CreateSiteLayoutDraftMutation,
  DiscardSiteLayoutDraftDocument,
  type DiscardSiteLayoutDraftInput,
  type DiscardSiteLayoutDraftMutation,
  Enforcement as EnforcementValue,
  type EntryFieldsFragment,
  type EntrySideFieldsFragment,
  EntrySidePosition as EntrySidePositionValue,
  GetSiteLayoutDraftDocument,
  type GetSiteLayoutDraftQuery,
  type GetSiteLayoutDraftQueryVariables,
  GetSiteLayoutStateDocument,
  type GetSiteLayoutStateQuery,
  type GetSiteLayoutStateQueryVariables,
  type LayoutSideInput,
  LayoutSideKind,
  PublishSiteLayoutDraftDocument,
  type PublishSiteLayoutDraftInput,
  type PublishSiteLayoutDraftMutation,
  GetSiteReaderDevicesDocument,
  type GetSiteReaderDevicesQuery,
  type GetSiteReaderDevicesQueryVariables,
  RemoveEntryDocument,
  type RemoveEntryInput,
  type RemoveEntryMutation,
  RemoveZoneDocument,
  type RemoveZoneInput,
  type RemoveZoneMutation,
  RenameEntryDocument,
  type RenameEntryInput,
  type RenameEntryMutation,
  RenameZoneDocument,
  type RenameZoneInput,
  type RenameZoneMutation,
  SetEntrySidesDocument,
  type SetEntrySidesInput,
  type SetEntrySidesMutation,
  SetSideEnforcementDocument,
  type SetSideEnforcementInput,
  type SetSideEnforcementMutation,
  type SiteLayoutFieldsFragment,
  type SiteLayoutRevisionFieldsFragment,
  UnassignReaderDocument,
  type UnassignReaderInput,
  type UnassignReaderMutation,
} from 'apollo/generated/client-operations';
import { NEW_ENTRY_NAME, NEW_ZONE_NAME, uniqueName } from './layoutRules';
import type { SiteLayoutClient } from './siteLayoutClient';
import {
  type AssignReaderInput,
  type Enforcement,
  type Entry,
  type EntrySide,
  type EntrySideKind,
  type EntrySidePatch,
  type EntrySidePosition,
  type Layout,
  type SiteLayoutDraft,
  SiteLayoutError,
  type SiteLayoutRevision,
  type SiteLayoutState,
  type Zone,
} from './types';

// The schema's enums arrive as the generated enum types; the designer's
// model spells the same values as string literals.
const kindOf = (kind: LayoutSideKind): EntrySideKind =>
  kind === LayoutSideKind.Zone ? 'ZONE' : 'OUTSIDE';
const enforcementOf = (enforcement: EnforcementValue): Enforcement =>
  enforcement === EnforcementValue.Enforced ? 'ENFORCED' : 'IDENTIFY_ONLY';
const positionOf = (position: EntrySidePositionValue): EntrySidePosition =>
  position === EntrySidePositionValue.A ? 'A' : 'B';
const enforcementInput = (enforcement: Enforcement): EnforcementValue =>
  enforcement === 'ENFORCED'
    ? EnforcementValue.Enforced
    : EnforcementValue.IdentifyOnly;
const positionInput = (position: EntrySidePosition): EntrySidePositionValue =>
  position === 'A' ? EntrySidePositionValue.A : EntrySidePositionValue.B;
const sideInput = (side: Pick<EntrySide, 'kind' | 'zoneId'>): LayoutSideInput =>
  side.kind === 'ZONE'
    ? { kind: LayoutSideKind.Zone, zoneId: side.zoneId }
    : { kind: LayoutSideKind.Outside };

const sideFrom = (side: EntrySideFieldsFragment): EntrySide => ({
  id: side.id,
  position: positionOf(side.position),
  kind: kindOf(side.kind),
  zoneId: side.zone?.id ?? null,
  enforcement: enforcementOf(side.enforcement),
});

const entryFrom = (entry: EntryFieldsFragment): Entry => ({
  id: entry.id,
  name: entry.name,
  sideA: sideFrom(entry.sideA),
  sideB: sideFrom(entry.sideB),
});

/** The designer's flat layout from one revision read whole. */
const layoutFrom = (layout: SiteLayoutFieldsFragment): Layout => ({
  zones: layout.zones.map(({ id, name }) => ({ id, name })),
  entries: layout.entries.map(entryFrom),
  readerAssignments: layout.entries.flatMap((entry) =>
    entry.assignments.map((assignment) => ({
      id: assignment.id,
      readerId: assignment.reader.id,
      readerDeviceId: assignment.reader.readerDeviceId,
      readerName: assignment.reader.name,
      entryId: entry.id,
      admitsIntoSideId: assignment.admitsInto.id,
    })),
  ),
});

const draftFrom = (layout: SiteLayoutFieldsFragment): SiteLayoutDraft => ({
  ...layoutFrom(layout),
  revisionId: layout.revisionId,
});

type ReadRevisionRow = SiteLayoutRevisionFieldsFragment & {
  layout: SiteLayoutFieldsFragment;
};

type PublishedRevisionRow = ReadRevisionRow & {
  publishedAt: string;
};

const isRead = (
  row: SiteLayoutRevisionFieldsFragment,
): row is ReadRevisionRow => row.layout != null;

const isPublished = (row: ReadRevisionRow): row is PublishedRevisionRow =>
  row.publishedAt != null;

/**
 * The site's history as the designer holds it: the draft apart, published
 * revisions oldest first and numbered from 1. The surface lists them newest
 * first with the draft, which has no `publishedAt`, ahead of them. A draft
 * discarded while the history was read has no layout; it is left out, as if
 * the read had come a moment later.
 */
const stateFrom = (
  rows: SiteLayoutRevisionFieldsFragment[],
): SiteLayoutState => {
  const readRows = rows.filter(isRead);
  const draftRow = readRows.find((row) => !isPublished(row));
  const published = readRows.filter(isPublished).reverse();
  const revisions: SiteLayoutRevision[] = published.map((row, index) => ({
    ...layoutFrom(row.layout),
    id: row.id,
    number: index + 1,
    publishedAt: row.publishedAt,
    closedAt: row.effectiveTo ?? null,
  }));
  return {
    draft: draftRow ? draftFrom(draftRow.layout) : null,
    revisions,
  };
};

/** How long one command may take, its several mutations included, before the client stops waiting. */
export const COMMAND_DEADLINE_MS = 30_000;

const stalledError = (): SiteLayoutError =>
  new SiteLayoutError(
    'STALLED',
    'A change is still waiting on the server. Refresh the page before making more changes.',
  );

const entryIn = (layout: Layout, entryId: string): Entry => {
  const entry = layout.entries.find((candidate) => candidate.id === entryId);
  if (!entry)
    throw new SiteLayoutError(
      'UNKNOWN_ENTRY',
      'That entry is not in this layout',
    );
  return entry;
};

const sideOf = (entry: Entry, sideId: string): EntrySide => {
  const side = [entry.sideA, entry.sideB].find(
    (candidate) => candidate.id === sideId,
  );
  if (!side)
    throw new SiteLayoutError(
      'UNKNOWN_SIDE',
      'That side is not a side of this entry',
    );
  return side;
};

/**
 * The layout designer's client over the `siteLayout` GraphQL surface. Each
 * edit is one of the surface's mutations against the site's draft, opened
 * from the effective layout when the site has none; the designer refetches
 * the state afterwards, so mutations answer with only what the caller uses.
 *
 * Nothing here goes through the Apollo cache: zones, entries and entry sides
 * keep their ids across revisions while their names and sides differ per
 * revision, so the cache's normalization by id would merge every revision
 * into one.
 *
 * Reader placement names the reader device the inventory shows; the surface
 * assigns its existing site registration. The picker and each placement read
 * M1's site inventory; this client never creates a reader registration.
 *
 * Commands run one at a time, each after the last has settled: an edit reads
 * the draft and then writes it (re-facing one side sends both, the first edit
 * opens the draft), so two at once would each write over what the other
 * read. Reads of the state do not wait.
 */
export class ApolloSiteLayoutClient implements SiteLayoutClient {
  private lastCommand: Promise<unknown> = Promise.resolve();

  private stalled = false;

  constructor(private readonly apollo: ApolloClient<object>) {
    // The Apollo client is injected through the parameter property.
  }

  async getState(siteId: string): Promise<SiteLayoutState> {
    const { data } = await this.apollo.query<
      GetSiteLayoutStateQuery,
      GetSiteLayoutStateQueryVariables
    >({
      query: GetSiteLayoutStateDocument,
      variables: { siteId },
      fetchPolicy: 'no-cache',
    });
    return stateFrom(data.getJobsite?.siteLayoutRevisions ?? []);
  }

  openDraft(siteId: string, fromRevisionId?: string): Promise<SiteLayoutDraft> {
    return this.inTurn(() => this.createDraft(siteId, fromRevisionId));
  }

  discardDraft(siteId: string): Promise<void> {
    return this.inTurn(async () => {
      await this.send<
        DiscardSiteLayoutDraftMutation,
        DiscardSiteLayoutDraftInput
      >(DiscardSiteLayoutDraftDocument, { siteId });
    });
  }

  publishDraft(siteId: string): Promise<void> {
    return this.inTurn(async () => {
      await this.send<
        PublishSiteLayoutDraftMutation,
        PublishSiteLayoutDraftInput
      >(PublishSiteLayoutDraftDocument, { siteId });
    });
  }

  addZone(siteId: string): Promise<Zone> {
    return this.inTurn(async () => {
      const draft = await this.draft(siteId);
      const { addZone } = await this.send<AddZoneMutation, AddZoneInput>(
        AddZoneDocument,
        { siteId, name: uniqueName(draft.zones, NEW_ZONE_NAME) },
      );
      return { id: addZone.id, name: addZone.name };
    });
  }

  renameZone(siteId: string, zoneId: string, name: string): Promise<Zone> {
    return this.inTurn(async () => {
      const { renameZone } = await this.send<
        RenameZoneMutation,
        RenameZoneInput
      >(RenameZoneDocument, { siteId, zoneId, name });
      return { id: renameZone.id, name: renameZone.name };
    });
  }

  removeZone(siteId: string, zoneId: string): Promise<void> {
    return this.inTurn(async () => {
      await this.send<RemoveZoneMutation, RemoveZoneInput>(RemoveZoneDocument, {
        siteId,
        zoneId,
      });
    });
  }

  /** A new entry joins the outside to the first zone, enforced on the zone side only. */
  addEntry(siteId: string): Promise<Entry> {
    return this.inTurn(async () => {
      const draft = await this.draft(siteId);
      const [firstZone] = draft.zones;
      if (!firstZone)
        throw new SiteLayoutError(
          'NO_ZONES',
          'Add a zone before adding an entry',
        );
      const { addEntry } = await this.send<AddEntryMutation, AddEntryInput>(
        AddEntryDocument,
        {
          siteId,
          name: uniqueName(draft.entries, NEW_ENTRY_NAME),
          sideA: {
            faces: { kind: LayoutSideKind.Outside },
            enforcement: EnforcementValue.IdentifyOnly,
          },
          sideB: {
            faces: { kind: LayoutSideKind.Zone, zoneId: firstZone.id },
            enforcement: EnforcementValue.Enforced,
          },
        },
      );
      return entryFrom(addEntry);
    });
  }

  renameEntry(siteId: string, entryId: string, name: string): Promise<Entry> {
    return this.inTurn(async () => {
      const { renameEntry } = await this.send<
        RenameEntryMutation,
        RenameEntryInput
      >(RenameEntryDocument, { siteId, entryId, name });
      return entryFrom(renameEntry);
    });
  }

  /**
   * What a side faces is set for both sides at once, the other side as it
   * is; enforcement is set for the one side, by its position.
   */
  updateEntrySide(
    siteId: string,
    entryId: string,
    sideId: string,
    patch: EntrySidePatch,
  ): Promise<Entry> {
    return this.inTurn(async () => {
      const draft = await this.draft(siteId);
      const entry = entryIn(draft, entryId);
      const side = sideOf(entry, sideId);
      if ('enforcement' in patch) {
        const { setSideEnforcement } = await this.send<
          SetSideEnforcementMutation,
          SetSideEnforcementInput
        >(SetSideEnforcementDocument, {
          siteId,
          entryId,
          position: positionInput(side.position),
          enforcement: enforcementInput(patch.enforcement),
        });
        return entryFrom(setSideEnforcement);
      }
      const faces: Pick<EntrySide, 'kind' | 'zoneId'> =
        patch.kind === 'ZONE'
          ? { kind: 'ZONE', zoneId: patch.zoneId }
          : { kind: 'OUTSIDE', zoneId: null };
      const { setEntrySides } = await this.send<
        SetEntrySidesMutation,
        SetEntrySidesInput
      >(SetEntrySidesDocument, {
        siteId,
        entryId,
        sideA: sideInput(side.position === 'A' ? faces : entry.sideA),
        sideB: sideInput(side.position === 'B' ? faces : entry.sideB),
      });
      return entryFrom(setEntrySides);
    });
  }

  removeEntry(siteId: string, entryId: string): Promise<void> {
    return this.inTurn(async () => {
      await this.send<RemoveEntryMutation, RemoveEntryInput>(
        RemoveEntryDocument,
        {
          siteId,
          entryId,
        },
      );
    });
  }

  /** Places a reader once: a reader placed elsewhere in the draft moves. */
  assignReader(
    siteId: string,
    { readerDeviceId, entryId, admitsIntoSideId }: AssignReaderInput,
  ): Promise<void> {
    return this.inTurn(async () => {
      const draft = await this.draft(siteId);
      const side = sideOf(entryIn(draft, entryId), admitsIntoSideId);
      const { data } = await this.apollo.query<
        GetSiteReaderDevicesQuery,
        GetSiteReaderDevicesQueryVariables
      >({
        query: GetSiteReaderDevicesDocument,
        variables: { jobsiteId: siteId },
        fetchPolicy: 'no-cache',
      });
      const reader = [
        ...data.siteDevices.flatMap((controller) => controller.readers),
        ...data.siteIndependentReaders,
        ...data.siteUnresolvedReaders,
      ].find((candidate) => candidate.readerDeviceId === readerDeviceId);
      if (!reader || reader.missing)
        throw new SiteLayoutError(
          'UNKNOWN_READER',
          'That reader is no longer available on this site. Reload the readers before placing it.',
        );
      const { readerId } = reader;
      const placed = draft.readerAssignments.some(
        (assignment) => assignment.readerId === readerId,
      );
      if (placed)
        await this.send<UnassignReaderMutation, UnassignReaderInput>(
          UnassignReaderDocument,
          { siteId, readerId },
        );
      await this.send<AssignReaderMutation, AssignReaderCommandInput>(
        AssignReaderDocument,
        { siteId, readerId, entryId, admitsInto: positionInput(side.position) },
      );
    });
  }

  unassignReader(siteId: string, readerDeviceId: string): Promise<void> {
    return this.inTurn(async () => {
      const draft = await this.draft(siteId);
      const assignment = draft.readerAssignments.find(
        (candidate) => candidate.readerDeviceId === readerDeviceId,
      );
      if (!assignment)
        throw new SiteLayoutError(
          'NOT_PLACED',
          'That reader is not placed in this layout',
        );
      await this.send<UnassignReaderMutation, UnassignReaderInput>(
        UnassignReaderDocument,
        { siteId, readerId: assignment.readerId },
      );
    });
  }

  /** Runs a command once the one before it has settled, whatever its outcome. */
  private inTurn<Result>(command: () => Promise<Result>): Promise<Result> {
    const result = this.lastCommand.then(() => this.withinDeadline(command));
    this.lastCommand = result.catch((): undefined => undefined);
    return result;
  }

  /**
   * A command that has not settled by the deadline stalls the client: whether
   * it reached the server is unknown, and every command after it would wait
   * behind it, so it and every later command fail as `STALLED` until the
   * page is reloaded.
   */
  private withinDeadline<Result>(
    command: () => Promise<Result>,
  ): Promise<Result> {
    if (this.stalled) return Promise.reject(stalledError());
    return new Promise<Result>((resolve, reject) => {
      const deadline = setTimeout(() => {
        this.stalled = true;
        reject(stalledError());
      }, COMMAND_DEADLINE_MS);
      command().then(
        (value) => {
          clearTimeout(deadline);
          resolve(value);
        },
        (error) => {
          clearTimeout(deadline);
          reject(error);
        },
      );
    });
  }

  private async createDraft(
    siteId: string,
    fromRevisionId?: string,
  ): Promise<SiteLayoutDraft> {
    const { createSiteLayoutDraft } = await this.send<
      CreateSiteLayoutDraftMutation,
      CreateSiteLayoutDraftInput
    >(CreateSiteLayoutDraftDocument, {
      siteId,
      basedOnRevisionId: fromRevisionId,
    });
    return draftFrom(createSiteLayoutDraft);
  }

  /** The site's draft, opened from its effective layout when it has none. */
  private async draft(siteId: string): Promise<SiteLayoutDraft> {
    const { data } = await this.apollo.query<
      GetSiteLayoutDraftQuery,
      GetSiteLayoutDraftQueryVariables
    >({
      query: GetSiteLayoutDraftDocument,
      variables: { siteId },
      fetchPolicy: 'no-cache',
    });
    const draft = data.getJobsite?.siteLayoutDraft;
    return draft ? draftFrom(draft) : this.createDraft(siteId);
  }

  /** Every mutation of the surface takes one `input`; a failed one throws its message. */
  private async send<Result, Input>(
    mutation: DocumentNode,
    input: Input,
  ): Promise<Result> {
    const { data } = await this.apollo.mutate<Result, { input: Input }>({
      mutation,
      variables: { input },
      fetchPolicy: 'no-cache',
    });
    if (!data) throw new Error('The layout command returned nothing');
    return data;
  }
}
